import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { Truck, Shield, Award, Users, CheckCircle2, Cpu, ArrowRight } from 'lucide-react';
import { siteImages } from '../data/mock';

const WhyChooseUsPage = () => {
  useEffect(() => { document.title = 'Why Choose Us | Work Supply House'; }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-80 overflow-hidden bg-dark-900">
          <img src={siteImages.aboutBanner} alt="Why Choose Us" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-900 via-dark-900/85 to-dark-900/40" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 rounded-full text-athletic-300 text-xs font-semibold mb-4">
                <Cpu className="w-3.5 h-3.5" /> OUR DIFFERENCE
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white font-bold mb-3">Why Choose Work Supply House?</h1>
              <p className="text-dark-200 max-w-lg">Curated supplies, fair prices and a shopping experience built around you.</p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main benefits */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: Truck, title: 'Fast USA Shipping', desc: 'Free USPS shipping on every order, delivered quickly to your door.' },
              { icon: Shield, title: 'Secure Checkout', desc: 'Your payment details stay protected with SSL encryption end to end.' },
              { icon: Award, title: 'Quality Products', desc: 'Every product earns its spot in our catalog before we ever list it.' },
              { icon: Users, title: 'Real Support', desc: 'A dedicated team ready to help you pick the right supplies for your desk.' },
            ].map((item, i) => (
              <div key={i} className="bg-dark-50 border border-dark-100 rounded-2xl p-6 hover:border-athletic-300 transition-all group">
                <div className="w-14 h-14 bg-athletic-500 rounded-xl flex items-center justify-center mb-5 group-hover:shadow-glow-athletic transition-shadow">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display font-bold text-dark-900 text-lg mb-2">{item.title}</h3>
                <p className="text-dark-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Additional features */}
          <div className="bg-dark-50 border border-dark-100 rounded-3xl p-8 md:p-12 mb-16">
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl font-bold text-dark-900 mb-4">The Work Supply House difference</h2>
              <p className="text-dark-500 max-w-2xl mx-auto">Here is what sets us apart from the big-box office-store crowd.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'A carefully curated product selection',
                'Premium quality at fair prices',
                '30-day hassle-free returns',
                'Fast processing and shipping',
                'Secure payment processing',
                'Clear, honest product descriptions',
                'Responsive customer support',
                'A clean, easy-to-use website',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-dark-100">
                  <CheckCircle2 className="w-5 h-5 text-athletic-600 flex-shrink-0" />
                  <span className="text-dark-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-br from-athletic-50 to-power-50 border border-athletic-100 rounded-3xl p-12">
            <h2 className="font-display text-3xl font-bold text-dark-900 mb-4">Ready to upgrade your setup?</h2>
            <p className="text-dark-600 mb-8 max-w-xl mx-auto">Browse our curated collection of office supplies and experience the Work Supply House difference.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-4 bg-athletic-500 text-white font-bold uppercase tracking-wider rounded-lg hover:opacity-90 transition-all shadow-button">
              Shop Now <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WhyChooseUsPage;
